
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import AppLayout from "@/components/layout/AppLayout";
import { useNavigate, useParams } from "react-router-dom";
import { TeamMissionsHeader } from "./components/TeamMissionsHeader";
import { MissionList } from "./components/MissionList";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";

interface Mission {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string | null;
  team_id: string;
  created_at: string;
  start_date: string | null;
  end_date: string | null;
  attachment_url: string | null;
  mission_type: string | null;
  starts_at: string | null;
  expires_at: string | null;
  is_expired: boolean | null;
}

interface TabData {
  id: string;
  label: string;
}

const TeamMissions = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin, roles } = useUserRoles();
  const isClient = roles.includes('client');

  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(teamId || '');

  const { data: teams } = useQuery({
    queryKey: ['teams', user?.id, isAdmin, isClient],
    queryFn: async () => {
      if (!user) return [];

      let query;
      
      if (isAdmin) {
        // Admins can see all teams
        query = supabase
          .from('teams')
          .select('id, name')
          .eq('status', 'active');
      } else if (isClient) {
        // Clients can see teams they're assigned to
        query = supabase
          .from('client_teams')
          .select('teams:team_id(id, name)')
          .eq('client_id', user.id);
      } else {
        // Guards can see teams they're members of
        query = supabase
          .from('team_members')
          .select('teams:team_id(id, name)')
          .eq('profile_id', user.id);
      }

      const { data, error } = await query;

      if (error) {
        toast({
          title: "Error loading teams",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }

      // Transform the data structure if needed for clients or guards
      if (isClient || (!isAdmin && !isClient)) {
        return data.map((item: any) => item.teams);
      }

      return data;
    },
    enabled: !!user,
  });

  // Check if user has access to the current team
  const teamAccess = useQuery({
    queryKey: ['teamAccess', teamId, user?.id, isClient, isAdmin],
    queryFn: async () => {
      if (!user || !teamId || !isValidUUID) return false;
      
      // If admin, always grant access
      if (isAdmin) return true;
      
      console.log("Checking team access for", isClient ? "client" : "guard", user.id, "for team", teamId);
      
      if (isClient) {
        const { data, error } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', user.id)
          .eq('team_id', teamId)
          .maybeSingle();
        
        if (error) {
          console.error('Error checking client team access:', error);
          return false;
        }
        
        console.log("Client team access check result:", !!data);
        return !!data;
      } else {
        // For guards
        const { data, error } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id)
          .eq('team_id', teamId)
          .maybeSingle();
        
        if (error) {
          console.error('Error checking guard team access:', error);
          return false;
        }
        
        return !!data;
      }
    },
    enabled: !!user && !!teamId && isValidUUID,
  });

  const { data: missions, isLoading, refetch } = useQuery({
    queryKey: ['missions', teamId, user?.id],
    queryFn: async () => {
      if (!isValidUUID || !user) {
        toast({
          title: "Invalid Team ID",
          description: "Please select a team",
          variant: "destructive",
        });
        return [];
      }

      // Check if user has access to this team
      if (!teamAccess.data && !isAdmin && !isClient) {
        toast({
          title: "Access Denied",
          description: "You don't have access to this team",
          variant: "destructive",
        });
        return [];
      }

      // First, mark any expired missions
      await supabase.rpc('mark_expired_missions');

      const { data, error } = await supabase
        .from('missions')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });

      if (error) {
        toast({
          title: "Error loading missions",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      return (data || []).map(m => ({
        ...m,
        attachment_url: null,
        mission_type: m.category,
        starts_at: m.start_date,
        expires_at: m.end_date
      })) as Mission[];
    },
    enabled: isValidUUID && Boolean(user) && Boolean(teamAccess.data !== undefined),
  });

  const handleAddMission = async (
    title: string, 
    description: string, 
    category: string, 
    attachmentUrl?: string,
    missionType?: string,
    startsAt?: string,
    expiresAt?: string
  ) => {
    if (!title || !category) {
      toast({
        title: "Missing Information",
        description: "Please provide both title and category",
        variant: "destructive",
      });
      return;
    }

    if (!isValidUUID) {
      toast({
        title: "Invalid Team ID",
        description: "Please select a team first",
        variant: "destructive",
      });
      return;
    }

    // Debug information to help troubleshoot
    console.log("Team access check:", {
      hasAccess: teamAccess.data,
      isClient,
      isAdmin,
      teamId,
      userId: user?.id
    });

    // Less strict access check - allow clients with team access or admins
    if (!isAdmin && !isClient && !teamAccess.data) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to add missions to this team",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('missions')
        .insert([
          {
            title,
            description,
            category,
            team_id: teamId,
            attachment_url: attachmentUrl,
            mission_type: missionType || 'continued',
            starts_at: startsAt,
            expires_at: expiresAt,
          },
        ]);

      if (error) {
        console.error('Error creating mission:', error);
        toast({
          title: "Error creating mission",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Mission created successfully",
      });

      refetch();
    } catch (err) {
      console.error('Error in handleAddMission:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleTeamChange = (selectedTeamId: string) => {
    navigate(`/missions/team/${selectedTeamId}`);
  };

  const handleMissionDelete = () => {
    refetch();
  };

  const tabs: TabData[] = [
    { id: "active", label: "Active Missions" },
    { id: "completed", label: "Completed" },
    { id: "expired", label: "Expired" },
  ];

  const filteredMissions = (status: string) => {
    return missions?.filter(mission => {
      if (status === 'active') {
        // Active missions: not completed, not cancelled, and not expired
        return mission.status !== 'completed' && 
               mission.status !== 'cancelled' && 
               mission.status !== 'expired' &&
               !mission.is_expired;
      } else if (status === 'completed') {
        return mission.status === 'completed';
      } else if (status === 'expired') {
        return mission.is_expired || mission.status === 'expired';
      }
      return false;
    }) || [];
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn px-8 py-8">
        <TeamMissionsHeader
          teams={teams}
          teamId={teamId}
          onTeamChange={handleTeamChange}
          isAdmin={isAdmin || isClient}
          onAddMission={handleAddMission}
        />

        <Tabs defaultValue={tabs[0].id} className="space-y-4">
          <TabsList className="bg-primary-100 text-primary-700">
            {tabs.map((tab) => (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id}>
              <MissionList
                missions={filteredMissions(tab.id)}
                isLoading={isLoading}
                isAdmin={isAdmin || isClient}
                onMissionDelete={handleMissionDelete}
              />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default TeamMissions;
